import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAutomationsApiFacade } from '../../../../apis/facades/useAutomationsApiFacade';
import { useDocumentationApiFacade } from '../../../../apis/facades/useDocumentationApiFacade';
import { useRollupsApiFacade } from '../../../../apis/facades/useRollupsApiFacade';
import {
  selectDependenciesHistoryLength,
  selectDependenciesConfigurationItem,
  setDependenciesConfigurationItem,
  setOpenAiDescriptionForRule,
  setConfigurationDependenciesForRule,
  setConfigurationUsedByForRule,
  setConfigurationUsedByForField,
  setLatestDeployment,
  clearDependencies,
  goBackInHistory,
  setSelectedDependencyContentType,
  setSourceCodeForConfiguration,
} from '../../../../reducers/documentation/documentationReducer';
import { telemetry } from '../../../../telemetry';
import extractErrorMsg from '../../../helpers/extractErrorMsg';
import { SweepElementTypes } from '../../types';
import { isFieldType, formatOpenAiResponse } from '../helpers';
import {
  SelectedDependencyWithClearHistoryProps,
  ConfigurationType,
  DependenciesContentViewType,
} from '../types';
import { useGetConfigurationItem } from './useGetConfigurationItem';
import { UserInputsByConfigurationId } from '@server/user-inputs';
import { useUserInputsApiFacade } from '../../../../apis/facades/useUserInputsApiFacade';
import { useLayouts } from '../../hooks/useLayouts';
import useParser from '../../../parser/useParser';
import { setSingleConfigurationUserInputs } from '../../../../reducers/documentation/userInputsReducer';
import { baseWhatDoesItDoTypes } from '../configuration-chat/configurationChatConsts';
import { selectParsedObjects } from '../../../../reducers/global/parserReducer';

export const useDependencies = (crmOrgId: string) => {
  const dispatch = useDispatch();

  const {
    post_crmOrgGenerateConfigurationGpt,
    get_crmOrgConfigurationConsumersFromSalto,
    get_crmOrgConfigurationDependenciesFromSalto,
    get_crmOrgFieldConsumerFromSalto,
    get_NaclFile,
  } = useDocumentationApiFacade();

  const { put_userInputsForConfiguration } = useUserInputsApiFacade();

  const { get_automation } = useAutomationsApiFacade();
  const { get_rollup } = useRollupsApiFacade();
  const { fetchLayoutData } = useLayouts();
  const { parseObjectOnDemand } = useParser({ crmOrgId });

  const parsedObjects = useSelector(selectParsedObjects(crmOrgId));
  const historyLength = useSelector(selectDependenciesHistoryLength);
  const dependenciesData = useSelector(selectDependenciesConfigurationItem);
  const selectedDependency = useSelector(selectDependenciesConfigurationItem);

  const firstHistoryItem = selectedDependency?.history?.[0];

  const configurationItem = useGetConfigurationItem({
    crmOrgId,
  });

  const handleSetConfigurationItem = useCallback(
    (props: SelectedDependencyWithClearHistoryProps) => {
      const {
        dependencyType,
        parentType,
        id,
        name,
        objectName,
        clearHistory,
        contentType,
        metadataId,
      } = props;
      if (isFieldType(parentType)) {
        dispatch(setDependenciesConfigurationItem(props));
        return;
      }

      if (parentType === ConfigurationType.layouts) {
        const isLayoutObjectParsed = parsedObjects?.find((_item) => _item.name === objectName); //if true layout is part of parsed objects already
        if (!isLayoutObjectParsed && metadataId) {
          fetchLayoutData(metadataId, crmOrgId);
        }
      }

      dispatch(
        setDependenciesConfigurationItem({
          id,
          dependencyType,
          parentType: parentType as ConfigurationType,
          name,
          clearHistory,
          objectName,
          contentType,
        }),
      );
    },
    [dispatch, fetchLayoutData, parsedObjects, crmOrgId],
  );

  const getOpenAiDescriptionForRule = useCallback(
    async ({
      objectName,
      ruleType,
      ruleName,
      ruleId,
      metadataId,
      crmOrgId,
    }: {
      objectName: string;
      ruleType: string;
      ruleName: string;
      ruleId: string;
      crmOrgId: string;
      metadataId: string;
    }) => {
      const isBaseConfigurationType = baseWhatDoesItDoTypes.includes(ruleType as ConfigurationType);
      const baseConfigurationTypePayload = {
        objectName,
        configurationKey: ruleType,
        configurationName: ruleName,
      };

      const otherConfigurationTypePayload = {
        metadataId,
        configurationName: ruleName,
      };

      try {
        const res = await post_crmOrgGenerateConfigurationGpt({
          crmOrgId,
          payload: isBaseConfigurationType
            ? baseConfigurationTypePayload
            : otherConfigurationTypePayload,
        });
        const openAiDescription = formatOpenAiResponse(res);

        dispatch(
          setOpenAiDescriptionForRule({
            crmOrgId,
            openAiDescription,
            ruleId,
          }),
        );
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
        dispatch(
          setOpenAiDescriptionForRule({
            crmOrgId,
            openAiDescription: {
              type: 'error',
              text: 'Something went wrong, please try again later',
            },
            ruleId,
          }),
        );
      }
    },
    [dispatch, post_crmOrgGenerateConfigurationGpt],
  );

  const getConfigurationDependencies = useCallback(
    async ({
      ruleType,
      crmOrgId,
      objectName,
      configurationName,
      metadataId,
      ruleMetadataId,
    }: {
      ruleType: string;
      crmOrgId: string;
      objectName: string;
      configurationName: string;
      metadataId: string;
      ruleMetadataId: string;
    }) => {
      try {
        const dependsOn = await get_crmOrgConfigurationDependenciesFromSalto({
          crmOrgId,
          configurationKey: ruleType,
          objectName,
          configurationName,
          metadataId,
        });

        dispatch(
          setConfigurationDependenciesForRule({
            crmOrgId,
            ruleMetadataId,
            dependsOn,
          }),
        );
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgConfigurationDependenciesFromSalto],
  );

  const getConfigurationUsedBy = useCallback(
    async ({
      ruleType,
      ruleMetadataId,
      crmOrgId,
      configurationName,
      objectName,
      metadataId,
    }: {
      ruleType: string;
      ruleMetadataId: string;
      crmOrgId: string;
      configurationName: string;
      objectName: string;
      metadataId: string;
    }) => {
      try {
        const usedBy = await get_crmOrgConfigurationConsumersFromSalto({
          crmOrgId,
          configurationKey: ruleType,
          configurationName,
          objectName,
          metadataId,
        });

        dispatch(
          setConfigurationUsedByForRule({
            crmOrgId,
            ruleMetadataId,
            usedBy,
          }),
        );
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgConfigurationConsumersFromSalto],
  );

  const getFieldConsumers = useCallback(
    async ({
      objectName,
      fieldName,
      crmOrgId,
      metadataId,
    }: {
      objectName: string;
      fieldName: string;
      crmOrgId: string;
      metadataId: string;
    }) => {
      try {
        const usedBy = await get_crmOrgFieldConsumerFromSalto({
          crmOrgId,
          objectName,
          fieldName,
          metadataId,
        });

        dispatch(
          setConfigurationUsedByForField({
            crmOrgId,
            usedBy,
            metadataId,
          }),
        );
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgFieldConsumerFromSalto],
  );

  const fetchLatestDeployedVersion = useCallback(
    async (sweepElementType: SweepElementTypes, elementId: string, crmOrgId: string) => {
      let element: any;

      switch (sweepElementType) {
        case ConfigurationType.sweepAutomation:
        case ConfigurationType.sweepAssignment:
        case ConfigurationType.sweepScheduledAssignment:
        case ConfigurationType.sweepAlert:
        case ConfigurationType.sweepMatching:
        case ConfigurationType.sweepDedupe:
          element = await get_automation(elementId, crmOrgId, true);
          break;
        case ConfigurationType.rollups:
          element = await get_rollup(elementId, crmOrgId, true);
          break;
        default:
          break;
      }

      if (element.readVersion) {
        dispatch(setLatestDeployment({ elementId, readOnlyElement: element.readVersion }));
        return;
      } else {
        telemetry.captureError(
          new Error('Read version was not returned for crmOrgId and element '),
          {
            crmOrgId,
            elementId,
          },
        );
        return;
      }
    },
    [dispatch, get_automation, get_rollup],
  );

  const updateDependencyTags = useCallback(
    (userInputs: UserInputsByConfigurationId, notParsedObjects?: string[]) => {
      try {
        dispatch(setSingleConfigurationUserInputs({ userInputs, crmOrgId }));
        put_userInputsForConfiguration({ crmOrgId, userInputs });

        if (notParsedObjects && notParsedObjects.length > 0) {
          notParsedObjects.forEach((objectName) => parseObjectOnDemand(objectName));
        }
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [crmOrgId, put_userInputsForConfiguration, dispatch, parseObjectOnDemand],
  );

  const handleCloseDependencies = useCallback(() => {
    dispatch(clearDependencies());
  }, [dispatch]);

  const onGoBackInHistory = useCallback(() => {
    dispatch(goBackInHistory());
  }, [dispatch]);

  const setContentType = useCallback(
    (contentType: DependenciesContentViewType) => {
      dispatch(setSelectedDependencyContentType({ contentType }));
    },
    [dispatch],
  );

  const onGoToDependenciesMainView = useCallback(() => {
    setContentType(DependenciesContentViewType.default);
  }, [setContentType]);

  const getSourceCode = useCallback(
    async (metadataId: string, configurationId: string) => {
      try {
        const sourceCode = await get_NaclFile({ crmOrgId, metadataId });
        dispatch(setSourceCodeForConfiguration({ crmOrgId, sourceCode, configurationId }));
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [crmOrgId, get_NaclFile, dispatch],
  );

  return {
    handleSetConfigurationItem,
    getOpenAiDescriptionForRule,
    updateDependencyTags,

    getConfigurationUsedBy,
    getConfigurationDependencies,
    getFieldConsumers,
    fetchLatestDeployedVersion,

    handleCloseDependencies,
    onGoBackInHistory,
    onGoToDependenciesMainView,
    setContentType,
    getSourceCode,

    historyLength,
    dependenciesData,
    configurationItem,
    activeItemId: firstHistoryItem?.id
      ? firstHistoryItem.id
      : firstHistoryItem?.name + (firstHistoryItem?.objectName ?? ''),
  };
};
